import { jsPDF } from "jspdf";
import html2canvas from "html2canvas";
import print from "print-js";

const invoiceHTMLToPDFBase64 = async (fileName, elId, printPDF) => {
  const element = elId ? elId : "cust-quote-invoice-display";
  const el = document.getElementById(element);

  // Measure DOM positions BEFORE capture (while element is in normal state)
  const elRect = el.getBoundingClientRect();
  const dpr = window.devicePixelRatio || 1;

  // Get all item row elements (works for both customer and admin views)
  const rowSelector =
    ".cust-quote-items-item, .cust-invoice-items-item, .client-invoice-quote-modal-items-item";
  const rows = el.querySelectorAll(rowSelector);

  // Build array of row bottom positions in canvas pixels
  const rowBottoms = [];
  rows.forEach((row) => {
    const rowRect = row.getBoundingClientRect();
    rowBottoms.push(Math.floor((rowRect.bottom - elRect.top) * dpr));
  });

  // Find the totals box position in canvas pixels
  const totalsEl =
    el.querySelector(".cust-quote-totals-box") ||
    el.querySelector(".cust-invoice-totals-box") ||
    el.querySelector(".client-invoice-quote-modal-totals-box");

  let totalsTopInCanvas = null;
  if (totalsEl) {
    const totalsRect = totalsEl.getBoundingClientRect();
    totalsTopInCanvas = Math.floor((totalsRect.top - elRect.top) * dpr);
  }

  // Temporarily remove border/shadow — we draw borders with jsPDF instead
  const origBorder = el.style.border;
  const origBoxShadow = el.style.boxShadow;
  const origBorderRadius = el.style.borderRadius;
  el.style.border = "none";
  el.style.boxShadow = "none";
  el.style.borderRadius = "0";

  const canvas = await html2canvas(el);

  // Restore original styles
  el.style.border = origBorder;
  el.style.boxShadow = origBoxShadow;
  el.style.borderRadius = origBorderRadius;

  // If totals wasn't found, default to end of canvas
  if (totalsTopInCanvas === null) {
    totalsTopInCanvas = canvas.height;
  }

  var pdf = new jsPDF("portrait", "px", "letter");
  var fullPageWidth = pdf.internal.pageSize.getWidth();
  var fullPageHeight = pdf.internal.pageSize.getHeight();
  var pageMargin = 5;
  var pageWidth = fullPageWidth - pageMargin * 2;
  var pageHeight = fullPageHeight - pageMargin * 2;

  var borderPad = 3;
  var contentWidth = pageWidth - borderPad * 2;
  var contentTopY = pageMargin + borderPad;
  var contentPageHeight = pageHeight - borderPad * 2;

  // Scale: canvas pixels -> PDF points
  const scale = contentWidth / canvas.width;
  const scaledHeight = canvas.height * scale;

  const drawPageBorder = () => {
    pdf.setDrawColor(0, 0, 0);
    pdf.setLineWidth(1.5);
    pdf.rect(pageMargin, pageMargin, pageWidth, pageHeight);
  };

  // Helper to slice a portion of the canvas and add it to the PDF
  const addSlice = (srcY, srcH, destY) => {
    if (srcH <= 0) return;
    const pageCanvas = document.createElement("canvas");
    pageCanvas.width = canvas.width;
    pageCanvas.height = Math.ceil(srcH);
    const ctx = pageCanvas.getContext("2d");
    ctx.drawImage(
      canvas,
      0, Math.floor(srcY), canvas.width, Math.ceil(srcH),
      0, 0, canvas.width, Math.ceil(srcH)
    );
    pdf.addImage(
      pageCanvas.toDataURL("image/jpeg"),
      "jpeg",
      pageMargin + borderPad,
      destY,
      contentWidth,
      srcH * scale
    );
  };

  if (scaledHeight <= contentPageHeight) {
    // Fits on one page — center vertically
    var marginY = (fullPageHeight - scaledHeight) / 2;
    pdf.addImage(
      canvas.toDataURL("image/jpeg"),
      "jpeg",
      pageMargin + borderPad,
      marginY,
      contentWidth,
      scaledHeight
    );
    drawPageBorder();
  } else {
    // Multi-page rendering
    const totalsHeight = canvas.height - totalsTopInCanvas;
    const scaledTotalsHeight = totalsHeight * scale;

    // Find clean page break points using row boundaries
    // Each cut is in canvas pixels
    const pageCuts = [0];
    let currentY = 0;

    while (currentY < totalsTopInCanvas) {
      const maxCanvasY = currentY + contentPageHeight / scale;

      if (maxCanvasY >= totalsTopInCanvas) {
        // Remaining items fit on this page
        pageCuts.push(totalsTopInCanvas);
        break;
      }

      // Find the last row bottom that fits entirely on this page
      let bestCut = null;
      for (let i = 0; i < rowBottoms.length; i++) {
        if (rowBottoms[i] > currentY && rowBottoms[i] <= maxCanvasY) {
          bestCut = rowBottoms[i];
        }
      }

      if (bestCut) {
        pageCuts.push(bestCut);
        currentY = bestCut;
      } else {
        // Fallback — no row boundary found
        pageCuts.push(Math.floor(maxCanvasY));
        currentY = Math.floor(maxCanvasY);
      }
    }

    // Render each page
    for (let i = 0; i < pageCuts.length - 1; i++) {
      if (i > 0) {
        pdf.addPage();
      }

      const sliceTop = pageCuts[i];
      const sliceBottom = pageCuts[i + 1];
      const sliceHeight = sliceBottom - sliceTop;

      // Draw the items slice at the top of the page
      addSlice(sliceTop, sliceHeight, contentTopY);

      const isLastItemsPage = i === pageCuts.length - 2;

      if (isLastItemsPage && totalsHeight > 0) {
        const destSliceHeight = sliceHeight * scale;
        const remainingSpace = contentPageHeight - destSliceHeight;

        if (scaledTotalsHeight <= remainingSpace) {
          // Totals fit on this page — place at bottom
          const totalsY =
            pageMargin + pageHeight - borderPad - scaledTotalsHeight;
          addSlice(totalsTopInCanvas, totalsHeight, totalsY);
        } else {
          // Totals need their own page
          drawPageBorder();
          pdf.addPage();

          const totalsY =
            pageMargin + pageHeight - borderPad - scaledTotalsHeight;
          addSlice(totalsTopInCanvas, totalsHeight, totalsY);
        }
      }

      drawPageBorder();
    }
  }

  if (printPDF) {
    const mobile = window.isMobile();

    if (mobile) {
      pdf.save(fileName);
      return true;
    }

    const data = pdf.output("blob");
    const blobUrl = URL.createObjectURL(data);
    print(blobUrl);
    return true;
  }

  return pdf.output("datauristring", { filename: fileName });
};

export default invoiceHTMLToPDFBase64;
